import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { useToast } from '../ui';

import { settingsAPI } from '../../services/api';
import useTelemetry from '../../hooks/useTelemetry';
import { normaliseLaborRates, serialiseLaborRates } from '../../quote-builder/laborMethods';
import PageHeader from '../ui/PageHeader';
import { useSettingsHref } from '../admin/DealerWorkspaceContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// Iter18 R2 §2.4 (OB-3 alignment): 6 seeded BIDIT categories. All 6 show by default;
// the operator hides categories they aren't using via the eye icon on each row.
// The `dimmed` flag is legacy — kept in the seed so migrations that carry it forward
// don't crash — but the visible dim state is now driven purely by `visibility[key] === false`.
const SEEDED_ITEM_CATEGORIES = [
  { key: 'spray_foam', label: 'Spray Foam' },
  { key: 'fiberglass', label: 'Fiberglass' },
  { key: 'bats',       label: 'Batts' },
  { key: 'flow',       label: 'Flow' },
  { key: 'accessories',label: 'Accessories' },
  { key: 'cellulose',  label: 'Cellulose' },
];

export default function MarginsPage() {
  const [margins, setMargins] = useState({});
  const [visibility, setVisibility] = useState({}); // Iter18 §2.7: per-category show/hide
  const [customCategories, setCustomCategories] = useState([]);
  const [laborRates, setLaborRates] = useState({ piece: { sq_ft: '', linear_ft: '' }, set: '', hourly: '', day: '' });
  // §2 (2026-08-07): master default margin — the LOWEST-tier fallback.
  // Previously hardcoded to 0.35 in QuoteBuilderPage, useQuoteMargins, vocabSeeds.
  // Now user-configurable. Stored as percentage (e.g. "35" for 35%).
  const [masterDefaultMargin, setMasterDefaultMargin] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { emit } = useTelemetry();
  const settingsHref = useSettingsHref(); // IDI-63
  const toast = useToast();

  const fetchMargins = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getMargins();
      if (res) {
        setMargins(res.category_margins || {});
        setVisibility(res.category_visibility || {});
        setCustomCategories(res.custom_categories || []);
        setLaborRates(normaliseLaborRates(res.labor_rates));
        // §2: hydrate master default. Backend stores as decimal (0.35);
        // UI shows percentage (35).
        if (res.master_default_margin != null && Number.isFinite(Number(res.master_default_margin))) {
          const n = Number(res.master_default_margin);
          // Normalise: >1 means it's already a percentage; <=1 means decimal.
          setMasterDefaultMargin(n > 1 ? String(n) : String(n * 100));
        }
      }
    } catch (err) {
      setError('Failed to load margins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMargins();
  }, [fetchMargins]);

  const handleMarginChange = (key, value) => {
    setMargins(prev => ({ ...prev, [key]: value }));
  };

  const handleLaborChange = (field, value) => {
    if (field === 'sq_ft' || field === 'linear_ft') {
      setLaborRates(prev => ({ ...prev, piece: { ...prev.piece, [field]: value } }));
    } else {
      setLaborRates(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await settingsAPI.updateMargins({
        category_margins: margins,
        category_visibility: visibility,
        custom_categories: customCategories,
        labor_rates: serialiseLaborRates(laborRates),
        // §2: store as decimal (0.35), UI collected as percentage ("35").
        master_default_margin: masterDefaultMargin !== ''
          ? Number(masterDefaultMargin) / 100
          : undefined,
      });
      toast.success('Margins saved');
      emit('settings.margins_updated', { categories: Object.keys(margins) });
    } catch (err) {
      setError('Failed to save margins');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3"><div className="h-8 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /><div className="h-32 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /></div>
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader
        title="Margins & Labor"
        subtitle="Set default margins by item category and configure labor rates."
      />

      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* §2 (2026-08-07): Master default margin. This is the LOWEST tier in the
          precedence chain and applies to any line whose category has no
          margin configured. Previously hardcoded to 0.35 in three places
          (QuoteBuilderPage, useQuoteMargins, vocabSeeds). Now user-configurable. */}
      <Card className="mb-6">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Master Default Margin</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Applied when a line has no category margin, customer margin, or line-item override.
            This is the last-resort fallback for pricing.
          </p>
        </div>
        <div className="p-4 flex items-center justify-between">
          <label htmlFor="master-default-margin" className="text-sm text-gray-700">
            Company default margin
          </label>
          <div className="flex items-center gap-2">
            <Input
              id="master-default-margin"
              type="number"
              min="0"
              max="99"
              step="0.5"
              value={masterDefaultMargin}
              onChange={(e) => setMasterDefaultMargin(e.target.value)}
              className="w-24 text-right"
              placeholder="35"
            />
            <span className="text-sm text-gray-500">%</span>
          </div>
        </div>
      </Card>

      {/* Iter18 §2.7 / OB-3: 6 seeded categories (5 shown + Cellulose dimmed).
          Per-row visibility toggle + "+ Add custom category". Margin-only — no markup. */}
      <Card className="mb-6">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Default Margin by Category</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            These defaults apply when no customer-specific or quote-line override is set.
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          {[...SEEDED_ITEM_CATEGORIES, ...customCategories].map((cat) => {
            const isHidden = visibility[cat.key] === false;
            const isCustom = cat.key.startsWith('custom_');
            const subtag = isCustom ? '— Custom' : '— BIDIT category';
            return (
              <div
                key={cat.key}
                className="flex items-center justify-between px-4 py-3"
              >
                <div className="flex-1 min-w-0" style={{ opacity: isHidden ? 0.4 : 1 }}>
                  <label htmlFor={`margin-${cat.key}`} className="text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>
                    {cat.label}{' '}
                    <span className="font-normal" style={{ color: 'var(--color-text-muted)' }}>
                      {subtag}{isHidden ? ' · HIDDEN' : ''}
                    </span>
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1" style={{ opacity: isHidden ? 0.4 : 1 }}>
                    <Input
                      id={`margin-${cat.key}`}
                      type="number"
                      min="0"
                      max="99"
                      step="0.5"
                      value={margins[cat.key] || ''}
                      onChange={(e) => handleMarginChange(cat.key, e.target.value)}
                      className="w-20 text-right"
                      placeholder="0"
                      disabled={isHidden}
                    />
                    <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>%</span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setVisibility((prev) => ({ ...prev, [cat.key]: !(prev[cat.key] === false ? false : true) }))
                    }
                    className="p-1.5 rounded hover:bg-[color:var(--neutral-100)]"
                    aria-label={isHidden ? `Show ${cat.label}` : `Hide ${cat.label}`}
                    title={isHidden ? 'Show category' : 'Hide category'}
                  >
                    {isHidden ? (
                      <EyeSlashIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
                    ) : (
                      <EyeIcon className="h-4 w-4" style={{ color: 'var(--color-text-default)' }} />
                    )}
                  </button>
                </div>
              </div>
            );
          })}

          {/* R20-26: Add custom category CTA removed. Custom items now propagate
              from the Item Catalog automatically. The margin
              field remains editable per-row; the entry point does not live here. */}
        </div>
      </Card>

      {/* Labor rates */}
      <Card className="mb-6">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Labor Defaults</h3>
          <p className="text-xs text-gray-500 mt-0.5">Starting rates for new sub-areas. Override per sub-area in Settings → Quote Areas.</p>
        </div>
        <div className="p-4 space-y-4">
          {/* 259 §3 Finding B: Piece Rate umbrella per Sarah's ruling */}
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Piece Rate</p>
          <div className="flex items-center justify-between">
            <label htmlFor="labor-sqft" className="text-sm text-gray-700">Per Square Foot</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <Input
                id="labor-sqft"
                type="number"
                min="0"
                step="0.01"
                value={laborRates.piece?.sq_ft || ''}
                onChange={(e) => handleLaborChange('sq_ft', e.target.value)}
                className="w-24 text-right"
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label htmlFor="labor-lft" className="text-sm text-gray-700">Per Linear Foot</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <Input
                id="labor-lft"
                type="number"
                min="0"
                step="0.01"
                value={laborRates.piece?.linear_ft || ''}
                onChange={(e) => handleLaborChange('linear_ft', e.target.value)}
                className="w-24 text-right"
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="border-t border-gray-100 pt-4 mt-4" />
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Set Rate</p>
          <div className="flex items-center justify-between">
            <label htmlFor="labor-set" className="text-sm text-gray-700">Per set</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <Input
                id="labor-set"
                type="number"
                min="0"
                step="0.01"
                value={laborRates.set || ''}
                onChange={(e) => handleLaborChange('set', e.target.value)}
                className="w-24 text-right"
                placeholder="0.00"
              />
            </div>
          </div>
          {/* R20-25: four labor rates total (Hourly + Day added) */}
          <div className="border-t border-gray-100 pt-4 mt-4" />
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Time-Based</p>
          <div className="flex items-center justify-between">
            <label htmlFor="labor-hourly" className="text-sm text-gray-700">Hourly</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <Input
                id="labor-hourly"
                type="number"
                min="0"
                step="0.01"
                value={laborRates.hourly || ''}
                onChange={(e) => handleLaborChange('hourly', e.target.value)}
                className="w-24 text-right"
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label htmlFor="labor-day" className="text-sm text-gray-700">Day Rate</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <Input
                id="labor-day"
                type="number"
                min="0"
                step="0.01"
                value={laborRates.day || ''}
                onChange={(e) => handleLaborChange('day', e.target.value)}
                className="w-24 text-right"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* B2 pointer to Quote Areas for per-sub-area overrides */}
      <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
        More detailed customisations are available in{' '}
        <a href={settingsHref('/settings/quote-areas')} className="underline" style={{ color: 'var(--color-action-primary)' }}>
          Settings → Quote Areas
        </a>.
      </p>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
